import React from 'react';
import { Instagram, Twitter, Facebook, Youtube } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    Shop: [
      { label: 'All Products', href: '#products' },
      { label: 'Subscriptions', href: '#subscriptions' },
      { label: 'Merch', href: '#' },
      { label: 'Gift Cards', href: '#' }
    ],
    Company: [
      { label: 'About Us', href: '#' },
      { label: 'Our Story', href: '#' },
      { label: 'Careers', href: '#' },
      { label: 'Press Kit', href: '#' }
    ],
    Support: [
      { label: 'Contact', href: '#contact' },
      { label: 'FAQs', href: '#' },
      { label: 'Shipping', href: '#' },
      { label: 'Returns', href: '#' }
    ],
    Legal: [
      { label: 'Privacy Policy', href: '#' },
      { label: 'Terms of Service', href: '#' },
      { label: 'Cookie Policy', href: '#' }
    ]
  };

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/teeazen?igsh=eXZkbmV1YmVjejc2', label: 'Instagram' },
    { icon: Twitter, href: 'https://x.com/TeeazeN2669?t=Fj1qjkoSLrxZVT31QBqgYw&s=03', label: 'X (Twitter)' },
    { icon: Facebook, href: 'https://www.facebook.com/share/1FZ9Juf79G/', label: 'Facebook' },
    { icon: Youtube, href: '#', label: 'YouTube' }
  ];

  return (
    <footer className="bg-black border-t border-zinc-800">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {/* Brand Column */}
            <div className="col-span-2 space-y-4">
              <a href="#" className="inline-block">
                <img 
                  src="https://customer-assets.emergentagent.com/job_drinkstack/artifacts/e9ixi8lt_Teeaze%20n%20Shotz.png" 
                  alt="TEEAZE N SHOTZ" 
                  className="h-24 w-auto"
                />
              </a>
              <p className="text-gray-400 max-w-xs">
                Juice for people who move different. No crash, no cap, just clean energy
                when you need it.
              </p>
              <div className="flex space-x-4">
                {socialLinks.map((social) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-zinc-900 hover:bg-amber-400 text-white hover:text-black p-2 rounded-lg transition-colors"
                      aria-label={social.label}
                    >
                      <Icon className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Links Columns */}
            {Object.entries(footerLinks).map(([title, links]) => (
              <div key={title}>
                <h3 className="text-white font-bold mb-4">{title}</h3>
                <ul className="space-y-2">
                  {links.map((link) => (
                    <li key={link.label}>
                      <a
                        href={link.href}
                        className="text-gray-400 hover:text-amber-400 transition-colors text-sm"
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="border-t border-zinc-800 py-8">
          <div className="max-w-md">
            <h3 className="text-white font-bold mb-2">Stay in the Loop</h3>
            <p className="text-gray-400 text-sm mb-4">
              Get first dibs on new drops, exclusive deals, and behind-the-scenes content.
            </p>
            <div className="flex space-x-2">
              <input
                type="email"
                placeholder="your.email@example.com"
                className="flex-1 bg-zinc-900 border border-zinc-800 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-amber-400"
              />
              <button className="bg-amber-400 hover:bg-amber-500 text-black font-bold px-6 py-2 rounded-lg transition-colors">
                Subscribe
              </button>
            </div>
          </div>
        </div>

        {/* Legal Disclaimer Section */}
        <div className="border-t border-zinc-800 py-8">
          <div className="max-w-5xl mx-auto space-y-6">
            {/* No Refund Policy */}
            <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-6">
              <h4 className="text-red-400 font-bold mb-3 text-sm flex items-center">
                <span className="text-xl mr-2">⚠️</span> NO REFUND POLICY - FOOD & BEVERAGE PRODUCTS
              </h4>
              <div className="text-xs text-gray-300 space-y-2 leading-relaxed">
                <p>
                  <strong className="text-white">ALL SALES FINAL:</strong> Due to health and safety regulations, all food and beverage products 
                  (including juices, mixed drinks, tea pouches, and any consumable items) are <strong className="text-red-400">NON-REFUNDABLE 
                  and NON-RETURNABLE</strong> once purchased. No exceptions.
                </p>
                <p>
                  <strong className="text-white">Apparel Only:</strong> T-shirts and hoodies may be exchanged for size or color within 7 days 
                  of delivery, provided items are unworn, unwashed, and in original condition with tags attached. Exchanges subject to availability. 
                  Shipping costs for exchanges are the responsibility of the customer.
                </p>
                <p>
                  <strong className="text-white">Damaged Items:</strong> If your order arrives damaged or defective, you must report it within 
                  24 hours of delivery with photographic evidence. We will assess claims on a case-by-case basis. This does not constitute a refund 
                  policy but rather a quality assurance measure.
                </p>
                <p className="text-red-400 font-semibold">
                  By completing your purchase, you acknowledge and agree to this no refund policy for all food and beverage products.
                </p>
              </div>
            </div>

            {/* Alcohol Policy */}
            <div className="bg-zinc-900/50 rounded-lg p-6">
              <h4 className="text-white font-bold mb-3 text-sm">Legal Disclaimer & Alcohol Policy</h4>
              <div className="text-xs text-gray-400 space-y-2 leading-relaxed">
                <p>
                  <strong className="text-white">Age Restriction:</strong> Products labeled as "Mixed Drink" may contain alcohol and are 
                  restricted to individuals 21 years of age or older in the United States. By purchasing these products, you affirm that 
                  you are of legal drinking age in your jurisdiction. We reserve the right to refuse service and cancel orders without refund 
                  if age verification cannot be completed.
                </p>
                <p>
                  <strong className="text-white">Delivery Policy:</strong> Adult signature (21+) required upon delivery for all orders 
                  containing alcoholic beverages. Valid government-issued photo identification must be presented. We cannot leave alcohol 
                  deliveries unattended.
                </p>
                <p>
                  <strong className="text-white">State Restrictions:</strong> We comply with all federal and state alcohol shipping laws. 
                  Some products may not be available for shipment to certain states. Shipping to P.O. boxes is prohibited for alcohol-containing products.
                </p>
                <p>
                  <strong className="text-white">Responsible Consumption:</strong> Please drink responsibly. Never drink and drive. 
                  If you or someone you know needs help with alcohol dependency, contact SAMHSA National Helpline: 1-800-662-HELP (4357).
                </p>
                <p className="text-gray-500 text-xs pt-2">
                  All information provided is for legal compliance purposes. TEEAZE N SHOTZ reserves the right to modify policies at any time. 
                  Continued use of this website constitutes acceptance of our terms and conditions.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-zinc-800 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © {currentYear} TEEAZE N SHOTZ. All rights reserved. Made with ♥ in Orlando.
            </p>
            <p className="text-gray-400 text-sm">
              Designed for wellness enthusiasts, by wellness enthusiasts.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};